import { splitChangelog } from '@erp/workflows';
import type { ReactNode } from 'react';
import { CaretDownIcon } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { T, useGT } from 'gt-react';

/** A commit subject stays visible; its body is only shown on request. */
export function ChangelogText({
  changelog,
  initiallyExpanded = false,
}: {
  changelog?: string | null;
  initiallyExpanded?: boolean;
}) {
  const { subject, body } = splitChangelog(changelog ?? '');
  if (!subject) {
    return (
      <span className="italic text-muted-foreground">
        <T context="workflow version without a changelog">No changelog yet.</T>
      </span>
    );
  }
  return (
    <div className="min-w-0 space-y-1 text-sm">
      <p className="break-words">{subject}</p>
      {body && (
        <details open={initiallyExpanded || undefined}>
          <summary className="w-fit cursor-pointer text-xs text-muted-foreground hover:text-foreground">
            <T context="expand the body of a workflow version changelog">
              Show details
            </T>
          </summary>
          <div className="mt-1 whitespace-pre-wrap break-words text-xs">
            {body}
          </div>
        </details>
      )}
    </div>
  );
}

/** Keeps compact version references navigable while offering their full message. */
export function WithChangelogDetails({
  children,
  changelog,
}: {
  children: ReactNode;
  changelog?: string | null;
}) {
  const gt = useGT();
  const { body } = splitChangelog(changelog ?? '');
  return (
    <span className="inline-flex items-center gap-0.5">
      {children}
      {body && (
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-5 w-5"
              onClick={(event) => event.stopPropagation()}
              aria-label={gt('Show Details', {
                context: 'show a compact workflow version changelog body',
              })}
            >
              <CaretDownIcon className="h-3 w-3" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-80"
            onClick={(event) => event.stopPropagation()}
          >
            <ChangelogText changelog={changelog} initiallyExpanded />
          </PopoverContent>
        </Popover>
      )}
    </span>
  );
}
